import { ErrorStackEntry, parseErrorStack } from '../utils/parse-error-stack.ts'
import { PNExceptionCode, PNExceptionCodeMap } from './pn-exception-code.ts'
import { ensureError } from '../utils/ensure-error.ts'

type PNExceptionBuilder = (...args: any[]) => PNException

/**
 * Generic SDK Exception used to differentiate known and handled exceptions.
 *
 * @internal
 */
export class PNException extends Error {
    public static _from(
        err: unknown,
        builder?: PNExceptionBuilder | null,
        code?: PNExceptionCode,
        isSuppressed: boolean = false,
    ): PNException {
        return err instanceof PNException
            ? err
            : builder
            ? builder(err)
            : new PNException(code, err === null ? null : ensureError(err), undefined, isSuppressed)
    }

    public static _filterBuilderStackEntry(stack: ErrorStackEntry[]): ErrorStackEntry[] {
        return stack.filter((entry) => entry.function !== `${PNException.name}.${PNException._from.name}`)
    }

    /**
     * Known Error names are always converted to a PNException to be
     * ignored downstream.
     *
     * `NotAllowedError` should not be ignored.
     */
    public static KNOWN_NAMES = ['AbortError', 'SecurityError', 'NetworkError']

    /**
     * Message patterns that should always be tracked even when KNOWN_NAMES is matched.
     */
    public static FORCE_ALLOW_MESSAGE_PATTERNS = [/no active service worker/i]

    public static _isSuppressed(error: PNException | Error): boolean {
        const name = error instanceof PNException ? error._name : error.name
        const message = error instanceof PNException ? error._compositeMessage : error.message

        const isSelfSuppressed = error instanceof PNException && error.isSuppressed
        const isKnownNameMatch = PNException.KNOWN_NAMES.includes(name)
        const isKnownNameMessageMatch = PNException.KNOWN_NAMES.some((kn) => new RegExp(kn).test(message))
        const isForceAllowedMsg = PNException.FORCE_ALLOW_MESSAGE_PATTERNS.some((rgx) => rgx.test(message))

        return !isForceAllowedMsg && (isSelfSuppressed || isKnownNameMatch || isKnownNameMessageMatch)
    }

    /**
     * Original error captured during creation
     * of the PNException.
     */
    public readonly originalError?: Error
    public readonly code: PNExceptionCode

    constructor(
        code?: PNExceptionCode,
        originalError?: Error | null,
        public readonly details?: string,
        public readonly isSuppressed: boolean = false,
    ) {
        code ??= PNExceptionCode.EXCEPTION

        const messageParts = [PNExceptionCodeMap[code].message]
        if (details) {
            messageParts.push(details)
        }

        super(messageParts.join(' - '))

        this.originalError = originalError ?? undefined
        this.code = code

        Object.setPrototypeOf(this, PNException.prototype)
        this.name = 'PNException'
    }

    /**
     * Returns the associated error message with an
     * appended message from the originalError, when
     * available.
     */
    public get _compositeMessage(): string {
        let message = this.message
        if (this.originalError instanceof PNException) {
            message = `${message.replace(/\.$/, '')}. ${this.originalError._compositeMessage}`
        } else if (this.originalError?.message && this.originalError?.message !== message) {
            message = `${message.replace(/\.$/, '')}. ${this.originalError?.message}`
        }
        return message
    }

    public get _isExitException(): boolean {
        return !!PNExceptionCodeMap[this.code].isExitException
    }

    public get _name(): string {
        return this.originalError?.name ?? this.name
    }

    public get _stack(): ErrorStackEntry[] {
        const originalStack = parseErrorStack(this.originalError?.stack, [PNException._filterBuilderStackEntry])
        const stack = parseErrorStack(this.stack, [PNException._filterBuilderStackEntry])
        return originalStack.length ? originalStack : stack
    }

    /** @override */
    public toString() {
        return this._compositeMessage
    }
}
